import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Text,
  Heading,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Alert,
  AlertIcon,
  useColorModeValue,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react';
import { calculateSampleSize } from '../utils/sampleSize';

function SampleSizeCalculator({ audienceSize, onSampleSizeChange }) {
  const [confidenceLevel, setConfidenceLevel] = useState(0.95);
  const [marginOfError, setMarginOfError] = useState(0.05);
  const [requiredSampleSize, setRequiredSampleSize] = useState(0);

  const bgColor = useColorModeValue('background.shade2');
  const borderColor = useColorModeValue('gray.300');
  const statBgColor = useColorModeValue('blue.50', 'blue.900');

  useEffect(() => {
    if (audienceSize > 0) {
      const sampleSize = calculateSampleSize(audienceSize, confidenceLevel, marginOfError);
      setRequiredSampleSize(sampleSize);

      // Notify parent component of the calculated sample size
      if (onSampleSizeChange) {
        onSampleSizeChange(sampleSize);
      }
    }
  }, [audienceSize, confidenceLevel, marginOfError, onSampleSizeChange]);

  const confidenceLevelOptions = [
    { value: 0.90, label: '90%', zScore: '1.645' },
    { value: 0.95, label: '95%', zScore: '1.96' },
    { value: 0.99, label: '99%', zScore: '2.576' },
  ];

  const marginOfErrorOptions = [
    { value: 0.03, label: '3%' },
    { value: 0.05, label: '5%' },
    { value: 0.10, label: '10%' },
  ];

  const getConfidenceLabel = () => {
    const option = confidenceLevelOptions.find(opt => opt.value === confidenceLevel);
    return option ? option.label : '';
  };

  const getMarginLabel = () => {
    const option = marginOfErrorOptions.find(opt => opt.value === marginOfError);
    return option ? option.label : '';
  };

  const getMethodologyExplanation = () => {
    return `With ${audienceSize.toLocaleString()} people in your audience, you need ${requiredSampleSize.toLocaleString()} responses to be ${getConfidenceLabel()} confident that your results represent the true population within ±${getMarginLabel()} margin of error.`;
  };

  const getRecommendationBadge = () => {
    if (marginOfError <= 0.03) {
      return { label: 'High Precision', color: 'purple' };
    } else if (confidenceLevel >= 0.99) {
      return { label: 'Conservative', color: 'blue' };
    } else if (confidenceLevel === 0.95 && marginOfError === 0.05) {
      return { label: 'Standard', color: 'green' };
    } else {
      return { label: 'Relaxed', color: 'orange' };
    }
  };

  const badge = getRecommendationBadge();

  if (!audienceSize || audienceSize === 0) {
    return null;
  }

  return (
    <Box bg={bgColor} p={6} borderRadius="md" border="1px solid" borderColor={borderColor} mb={6}>
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between" align="center">
          <Heading as="h3" size="md">
            Statistical Sample Size Calculator
          </Heading>
          <Box
            px={3}
            py={1}
            bg={`${badge.color}.100`}
            color={`${badge.color}.800`}
            borderRadius="md"
            fontSize="sm"
            fontWeight="bold"
          >
            {badge.label}
          </Box>
        </HStack>

        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            {getMethodologyExplanation()}
          </Text>
        </Alert>

        {/* Required Sample Size Display */}
        <Box bg={statBgColor} p={4} borderRadius="md" textAlign="center">
          <Stat>
            <StatLabel fontSize="sm">Required Sample Size</StatLabel>
            <StatNumber fontSize="4xl" color="blue.600">
              {requiredSampleSize.toLocaleString()}
            </StatNumber>
            <StatHelpText fontSize="xs">
              responses needed for statistical significance
            </StatHelpText>
          </Stat>
        </Box>

        {/* Confidence Level Slider */}
        <FormControl>
          <FormLabel>
            Confidence Level: <strong>{getConfidenceLabel()}</strong>
          </FormLabel>
          <Text fontSize="xs" color="gray.600" mb={2}>
            Higher confidence = more certainty your results are accurate
          </Text>
          <Slider
            value={confidenceLevelOptions.findIndex(opt => opt.value === confidenceLevel)}
            min={0}
            max={confidenceLevelOptions.length - 1}
            step={1}
            onChange={(index) => setConfidenceLevel(confidenceLevelOptions[index].value)}
            colorScheme="blue"
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb boxSize={6} />
          </Slider>
          <HStack justify="space-between" mt={2}>
            {confidenceLevelOptions.map((option) => (
              <Text
                key={option.value}
                fontSize="xs"
                color={option.value === confidenceLevel ? 'blue.600' : 'gray.500'}
                fontWeight={option.value === confidenceLevel ? 'bold' : 'normal'}
              >
                {option.label}
              </Text>
            ))}
          </HStack>
        </FormControl>

        {/* Margin of Error Slider */}
        <FormControl>
          <FormLabel>
            Margin of Error: <strong>±{getMarginLabel()}</strong>
          </FormLabel>
          <Text fontSize="xs" color="gray.600" mb={2}>
            Lower margin = more precise results (but requires more responses)
          </Text>
          <Slider
            value={marginOfErrorOptions.findIndex(opt => opt.value === marginOfError)}
            min={0}
            max={marginOfErrorOptions.length - 1}
            step={1}
            onChange={(index) => setMarginOfError(marginOfErrorOptions[index].value)}
            colorScheme="teal"
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb boxSize={6} />
          </Slider>
          <HStack justify="space-between" mt={2}>
            {marginOfErrorOptions.map((option) => (
              <Text
                key={option.value}
                fontSize="xs"
                color={option.value === marginOfError ? 'teal.600' : 'gray.500'}
                fontWeight={option.value === marginOfError ? 'bold' : 'normal'}
              >
                ±{option.label}
              </Text>
            ))}
          </HStack>
        </FormControl>

        {/* Additional Context */}
        <Box fontSize="xs" color="gray.600" borderTop="1px solid" borderColor={borderColor} pt={4}>
          <Text mb={2}>
            <strong>Methodology:</strong> Uses standard statistical formula with finite population correction
            (Cochran 1977). Assumes maximum variability (p=0.5) for conservative estimates.
          </Text>
          <Text>
            <strong>Audience size:</strong> {audienceSize.toLocaleString()} people
          </Text>
        </Box>
      </VStack>
    </Box>
  );
}

export default SampleSizeCalculator;
